import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, X, UserPlus, MessageCircleOff, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export function UpgradePrompt({ feature, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [guestTrialConfig, setGuestTrialConfig] = useState(null);

  const isGuestLimit = feature === 'guest_limit';

  useEffect(() => {
    if (!isGuestLimit) return;
    axios.get(`${API}/guest-trial-config`).then(res => setGuestTrialConfig(res.data)).catch(() => {});
  }, [isGuestLimit]);

  const featureNames = {
    youtube_analysis: t('upgrade.youtube', 'YouTube Analysis'),
    file_upload: t('upgrade.file_upload', 'File Upload'),
    recap: t('upgrade.recap', 'Weekly Recap'),
    themes: t('upgrade.themes', 'All Themes'),
    push_notifications: t('upgrade.notifications', 'Push Notifications'),
    daily_limit: t('upgrade.daily_limit', 'Unlimited Messages'),
    guest_limit: t('upgrade.guest_limit', 'Guest Message Limit'),
    gospel_lens: t('upgrade.gospel_lens', 'Gospel Lens'),
    commentators: t('upgrade.commentators', 'Commentary Library'),
    greek_analysis: t('upgrade.greek_analysis', 'Greek-English Analysis'),
    historical_context: t('upgrade.historical_context', 'Historical Context'),
    deep_study: t('upgrade.deep_study', 'Deep Study Mode'),
  };

  const handleViewPlans = () => {
    if (onClose) onClose();
    navigate('/plans');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="upgrade-prompt">
      <div className="relative mx-4 w-full max-w-sm rounded-2xl p-6 text-center"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}>
        {onClose && (
          <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition" data-testid="upgrade-close-btn">
            <X className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
          </button>
        )}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: 'var(--theme-accent-light)' }}>
          {isGuestLimit ? <UserPlus className="h-7 w-7" style={{ color: 'var(--theme-accent)' }} /> : <Lock className="h-7 w-7" style={{ color: 'var(--theme-accent)' }} />}
        </div>
        {isGuestLimit ? (
          <>
            <h3 className="mb-2 text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('upgrade.guest_title', 'Want to Keep Exploring?')}
            </h3>
            <p className="mb-5 text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
              {guestTrialConfig?.enabled
                ? t('upgrade.guest_desc_trial', `Create a free account to get more daily messages and enjoy a free ${guestTrialConfig.trial_duration_days}-day trial of all premium features!`)
                : t('upgrade.guest_desc', 'Create a free account to get more messages each day. It only takes a moment!')}
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => { if (onClose) onClose(); navigate(guestTrialConfig?.enabled ? '/signup?trial=true&src=upgrade' : '/signup'); }}
                className="w-full gap-2 rounded-xl py-5 font-medium"
                style={{ background: guestTrialConfig?.enabled ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'var(--theme-accent)', color: '#fff' }}
                data-testid="guest-signup-btn"
              >
                {guestTrialConfig?.enabled ? <Gift className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                {guestTrialConfig?.enabled
                  ? t('upgrade.create_account_trial', `Sign Up Free — ${guestTrialConfig.trial_duration_days}-Day Trial Included`)
                  : t('upgrade.create_account', 'Create Free Account')}
              </Button>
              {onClose && (
                <Button variant="ghost" className="w-full rounded-xl" onClick={onClose} style={{ color: 'var(--theme-text-secondary)' }}>
                  {t('upgrade.later', 'Maybe Later')}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <h3 className="mb-2 text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('upgrade.title', 'Upgrade to Unlock')}
            </h3>
            <p className="mb-5 text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
              {featureNames[feature] || feature} {t('upgrade.requires_paid', 'requires a paid subscription.')}
            </p>
            <div className="space-y-2">
              <Button
                onClick={handleViewPlans}
                className="w-full gap-2 rounded-xl py-5 font-medium"
                style={{ background: 'var(--theme-accent)', color: '#fff' }}
                data-testid="upgrade-btn"
              >
                <Sparkles className="h-4 w-4" />
                {t('upgrade.button', 'View Plans')}
              </Button>
              {onClose && (
                <Button variant="ghost" className="w-full rounded-xl" onClick={onClose} style={{ color: 'var(--theme-text-secondary)' }}>
                  {t('upgrade.later', 'Maybe Later')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function MessageLimitBanner({ used, limit, isGuest = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remaining = Math.max(0, limit - used);
  const percentage = limit > 0 ? (used / limit) * 100 : 0;
  const isExhausted = remaining === 0;
  const [guestTrialConfig, setGuestTrialConfig] = useState(null);
  
  // Live countdown state — updates every minute
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const calcTime = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      tomorrow.setUTCHours(0, 0, 0, 0);
      const diff = tomorrow - now;
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };
    setTimeLeft(calcTime());
    const interval = setInterval(() => setTimeLeft(calcTime()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isGuest && isExhausted) {
      axios.get(`${API}/guest-trial-config`).then(res => setGuestTrialConfig(res.data)).catch(() => {});
    }
  }, [isGuest, isExhausted]);

  // Gradient color based on usage percentage
  const getColor = () => {
    if (percentage <= 30) return '#22c55e'; // Green
    if (percentage <= 50) return '#84cc16'; // Lime
    if (percentage <= 70) return '#eab308'; // Yellow
    if (percentage <= 85) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };
  
  const color = getColor();

  if (limit === -1) return null; // Unlimited

  if (isExhausted) {
    if (isGuest) {
      return (
        <div 
          className="p-4 rounded-2xl text-center space-y-3"
          style={{
            background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.2)',
          }}
          data-testid="guest-limit-exhausted"
        >
          <div className="flex items-center justify-center gap-2">
            <UserPlus className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <span className="font-medium" style={{ color: 'var(--theme-accent)' }}>
              {t('upgrade.guest_banner_title', 'Want more messages?')}
            </span>
          </div>
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {guestTrialConfig?.enabled
              ? t('upgrade.guest_banner_trial', `Sign up for a free account and get a ${guestTrialConfig.trial_duration_days}-day premium trial!`)
              : t('upgrade.guest_banner', 'Create a free account to get more messages each day.')}
          </p>
          <button
            onClick={() => navigate(guestTrialConfig?.enabled ? '/signup?trial=true&src=upgrade' : '/signup')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all hover:scale-105"
            style={{ background: guestTrialConfig?.enabled ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'var(--theme-accent)', color: '#fff' }}
            data-testid="guest-signup-banner-btn"
          >
            {guestTrialConfig?.enabled ? <Gift className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
            {guestTrialConfig?.enabled
              ? t('upgrade.signup_trial_btn', 'Sign Up Free — Trial Included')
              : t('upgrade.signup_btn', 'Create Free Account')}
          </button>
        </div>
      );
    }

    return (
      <div 
        className="p-4 rounded-2xl text-center space-y-3"
        style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)',
        }}
        data-testid="message-limit-exhausted"
      >
        <div className="flex items-center justify-center gap-2">
          <MessageCircleOff className="h-5 w-5 text-red-400" />
          <span className="font-medium text-red-400">
            {t('upgrade.daily_limit_reached', 'Daily Limit Reached')}
          </span>
        </div>
        <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
          {t('upgrade.reset_countdown', 'Resets in {{time}} — come back soon!', { time: timeLeft })}
        </p>
        <button
          onClick={() => navigate('/plans')}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#000' }}
          data-testid="upgrade-unlimited-btn"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('upgrade.unlock_unlimited', 'Upgrade for Unlimited Messages')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs"
      style={{
        background: 'var(--glass-bg)',
        border: `1px solid ${percentage > 70 ? `${color}40` : 'var(--glass-border)'}`,
      }}
      data-testid="message-limit-banner"
    >
      <div className="flex-1">
        <div className="flex justify-between mb-1.5">
          <span style={{ color: 'var(--theme-textSecondary)' }}>{t('upgrade.messages_today', 'Messages today')}</span>
          <span className="font-medium" style={{ color }}>
            {remaining} {t('upgrade.remaining', 'remaining')}
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--glass-border)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(percentage, 100)}%`,
              background: color,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function FeatureGate({ feature, children, fallback }) {
  // Import inline to avoid circular deps
  const { useSubscription } = require('@/contexts/SubscriptionContext');
  const { hasFeature } = useSubscription();

  if (hasFeature(feature)) {
    return children;
  }

  return fallback || null;
}
